import { Route } from "vue-router";
import compact from "lodash/compact";
import { default as JSONPath } from 'fast-json-patch';
import moment from "moment";

import { itemStateConstant } from "../common";
import { PropertyMultModel } from "../models/property";
import { IPropertyMultModel } from '../models/property/property-mult.model';
import { DEFAULT_NUMBER_DECIMAL, ROUTER, CONSTRUCTION_TYPE, RESOURCE_LINKS, LOCALSTORAGE_ENV } from "../common/constants";
import moduleConstant from "../common/module-constant";
import CACHE_KEY from "../common/cache-key.constant";
import { LocalforageWrapper } from "./localforage-wrapper";
import i18n from '../i18n';
import { TAB_TREE } from "../common/tab-tree.constants";


import { 
    LOCALSTORAGE_BASE_ICON_RESOURCE_URL,
    OMNICASA_MAP_BASE_URL,
} from '../common/constants'

export function find(list, f) {
    return list.filter(f)[0]
}

export function deepCopy(obj, cache = []) {
    // just return if obj is immutable value
    if (obj === null || typeof obj !== 'object') {
        return obj
    }

    // if obj is hit, it is in circular structure
    const hit = find(cache, c => c.original === obj)
    if (hit) {
        return hit.copy
    }

    const copy = Array.isArray(obj) ? [] : {}
    // put the copy into cache at first
    // because we want to refer it in recursive deepCopy
    cache.push({
        original: obj,
        copy
    })

    Object.keys(obj).forEach(key => {
        copy[key] = deepCopy(obj[key], cache)
    })

    return copy
}

export function handlerPropertyMults(originPropertyMults: IPropertyMultModel[], values: Array<any>, mainId: number) {
    var obj = []
    if (values.length <= 0) {
        obj = originPropertyMults.filter(item => item.PropertyId)
            .map(item => {
                item.ItemState = itemStateConstant.Deleted
                if (item.IsMain) {
                    item.IsMain = false
                }
                return item
            })
    } else if (originPropertyMults.length <= 0) {
        values.forEach(item => {
            let propertyMult = new PropertyMultModel({
                FieldId: item.Id,
                ItemState: itemStateConstant.Added,
                IsMain: mainId == item.Id ? true : false
            })
            obj.push(propertyMult)
        })
    } else {
        let propertyCurrents = originPropertyMults.filter(item => {
            let env = values.find(val => val.Id == item.FieldId)
            return env ? true : false
        })

        if (propertyCurrents.length > 0) {
            obj = originPropertyMults.map(item => {
                let propertyCurrent = propertyCurrents.find(obj => obj.FieldId == item.FieldId)
                if (propertyCurrent) {
                    item.ItemState = propertyCurrent.PropertyId ? itemStateConstant.Modified : propertyCurrent.ItemState
                    item.IsMain = mainId == item.FieldId ? true : false
                    return item
                }
                item.ItemState = itemStateConstant.Deleted
                item.IsMain = mainId == item.FieldId ? true : false
                return item
            }).filter(item => {
                if (item.ItemState == itemStateConstant.Deleted)
                    return item.PropertyId ? true : false
                return true;
            })
        } else {
            obj = originPropertyMults.filter(item => item.PropertyId).map(item => {
                item.ItemState = itemStateConstant.Deleted
                if (item.IsMain) mainId = -1
                return item
            })
        }

        let valueChanges = values.filter(item => {
            let val = originPropertyMults.find(obj => obj.FieldId == item.Id)
            return val ? false : true
        })
        if (valueChanges.length > 0) {
            valueChanges.forEach(item => {
                let propertyMult = new PropertyMultModel({
                    FieldId: item.Id,
                    ItemState: itemStateConstant.Added,
                    IsMain: mainId == item.Id ? true : false
                })
                obj.push(propertyMult)
            })
        }

    }
    return obj
}

const isObject = function (obj) {
    return obj !== null && typeof obj === 'object';
};
export const getValueByPath = function (object, prop) {
    prop = prop || '';
    const paths = prop.split('.');
    let current = object;
    let result = null;
    for (let i = 0, j = paths.length; i < j; i++) {
        const path = paths[i];
        if (!current) break;

        if (i === j - 1) {
            result = current[path];
            break;
        }
        current = current[path];
    }
    return result;
};

export function orderBy(array, sortKey?: any, reverse?: any, sortMethod?: any, sortBy?: any) {
    if (!array || array.length === 0) return array;
    if (!sortKey && !sortMethod && (!sortBy || Array.isArray(sortBy) && !sortBy.length)) {
        return array;
    }
    if (typeof reverse === 'string') {
        reverse = reverse === 'descending' ? -1 : 1;
    } else {
        reverse = (reverse && reverse < 0) ? -1 : 1;
    }
    const getKey = sortMethod ? null : function (value, index) {
        if (sortBy) {
            if (!Array.isArray(sortBy)) {
                sortBy = [sortBy];
            }
            return sortBy.map(function (by) {
                if (typeof by === 'string') {
                    return getValueByPath(value, by);
                } else {
                    return by(value, index, array);
                }
            });
        }
        if (sortKey !== '$key') {
            if (isObject(value) && '$value' in value) value = value.$value;
        }
        return [isObject(value) ? getValueByPath(value, sortKey) : value];
    };
    const compare = function (a, b) {
        if (sortMethod) {
            return sortMethod(a.value, b.value);
        }
        for (let i = 0, len = a.key.length; i < len; i++) {
            if (a.key[i] === undefined) return 1;
            if (b.key[i] === undefined || b.key[i] === null) return -1; 
            if (a.key[i].trim() < b.key[i].trim()) {
                return -1;
            }
            if (a.key[i].trim() > b.key[i].trim()) {
                return 1;
            }
        }
        return 0;
    };
    return array.map(function (value, index) {
        return {
            value: value,
            index: index,
            key: getKey ? getKey(value, index) : null
        };
    }).sort(function (a, b) {
        let order = compare(a, b);
        if (!order) {
            order = a.index - b.index;
        }
        return order * reverse;
    }).map(item => item.value);
}

export function isArray(obj: any): boolean { return Array.isArray(obj) }
export function filterForTree(value: string, data: object, key: string = 'label') {
    if (!value) return true;
    return data[key].toUpperCase().indexOf(value.toUpperCase()) !== -1;
}
export function splitConfirm(value: string): { title: string, body: string } {
    if (value == null) return { title: '', body: '' }
    let arr = value.split('|') || [];

    return {
        title: arr.length > 1 ? arr[1] : '',
        body: arr.length >= 0 ? arr[0] : ''
    }
}
export function compareObject(obj: object | null | undefined, objOrigin: object | null | undefined) {
    try {
        if (!obj || !objOrigin || typeof obj !== 'object' || typeof objOrigin !== 'object') {
            return false;
        }
        let result: Array<any> = JSONPath.compare(obj, objOrigin);
        return result.length > 0;
    } catch {
        return false;
    }
    
}
export function compareObjectAndApplyNew(obj: object, objOrigin: object) {
    if (typeof obj !== 'object' || typeof objOrigin !== 'object') return objOrigin;
    const cloneObj = deepCopy(obj);
    const cloneObjOrigin = deepCopy(objOrigin);
    let newObj = Object.assign(cloneObjOrigin, cloneObj);
    return newObj;
}
export function getDefaultSetting() {
    let langCode = localStorage.getItem("lang");
    let widthInner = ((420 * 100) / window.innerWidth)
    if (langCode === 'fr-BE') {
        widthInner = ((450 * 100) / window.innerWidth)
    }
    return {
        widthInnerDialog: widthInner + '%'
    }
}
export function getDefaultSettingModalHeight() {
    return {
        height: window.innerHeight * 0.6 + "px",
        overflowX: "hidden",
        overflowY: "auto",
        position: "relative"
    };
}
export function getDefaultScreenSize() {
    return {
        height: window.innerHeight,
        width: window.innerWidth
    }
}

export function getDefaultWidthforModal(defaultWidth: number = 1200) {
    let width = window.innerWidth;
    let widthModal = ((defaultWidth * 100) / width)

    return {
        widthModal: widthModal >= 62.5 ? (62.5 + '%') : '100%'
    }
}

export function parseTime(time: string) {
    const values = (time || '').split(':');
    if (values.length >= 2) {
        const hours = parseInt(values[0], 10);
        const minutes = parseInt(values[1], 10);
        return formatTime({ hours, minutes })
    }
    return '00:00';
}

export function formatTime(time) {
    return (time.hours < 10 ? '0' + time.hours : time.hours) + ':' + (time.minutes < 10 ? '0' + time.minutes : time.minutes);
};

export function maskDefault(props: { autoGroup: boolean | false, type: string | 'decimal', allowMinus: boolean | true, locale: string }) {
    let pattern = Intl.NumberFormat(props.locale).format(-1234.56);
    let format = pattern.split("").filter(num => !!!parseInt(num));

    return {
        alias: 'numeric',
        rightAlign: true,
        groupSeparator: format[1],
        radixPoint: format[2],
        autoGroup: props.autoGroup,
        digits: props.type === 'decimal' ? DEFAULT_NUMBER_DECIMAL.maximumFractionDigits : DEFAULT_NUMBER_DECIMAL.minimumFractionDigits,
        allowMinus: props.allowMinus,
        // integerDigits: 5
    }
}
export function getFormatNumberByLocale(locale: string) {
    let pattern = Intl.NumberFormat(locale).format(-1234.56);
    return pattern.split("").filter(num => !!!parseInt(num));
}

export function convertStringForInputNumber(value: string, locale: string) {
    try {
        let format = getFormatNumberByLocale(locale);
        let newValue = value.split(format[1]).join("").replace(format[2], '.');
        return newValue;
    } catch (e) {
        return '';
    }
}

export function formatNumberByLocale(value: number, locale: string, type?: string | 'decimal') {
    try {
        return Intl.NumberFormat(locale, {
            maximumFractionDigits: DEFAULT_NUMBER_DECIMAL.maximumFractionDigits
        }).format(value)
    } catch (e) {
        return ''
    }
}

export interface IMaskProperty {
    autoGroup: boolean | false;
    type: string | 'decimal';
    allowMinus: boolean | true;
    locale: string
}

export class MaskProperty implements IMaskProperty {
    autoGroup: boolean | false;
    type: string | 'decimal';
    allowMinus: boolean | true;
    locale: string;

    constructor(props: IMaskProperty) {
        this.autoGroup = props.autoGroup;
        this.type = props.type;
        this.allowMinus = props.allowMinus;
        this.locale = props.locale
    }
}

export function isTouchDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent);
}

/**
 * Escape HTML special characters to prevent XSS attacks
 * @param str - The string to escape
 * @returns Escaped string safe for HTML insertion
 */
export function escapeHtml(str: string): string {
    if (!str) return '';
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

export function compilerAutocomplete(props: any, query: string) {
    let { Id, Reference, Email, icon, PhoneNumber, Price } = props;
    let title: string = '';
    // Escape all user-provided values to prevent XSS
    if (icon) { title = escapeHtml(icon) + ' '; }
    if (Reference) { title += escapeHtml(Reference) + ' '; }
    if (Email) { title += ', ' + escapeHtml(Email); }
    if (PhoneNumber) { title += ', ' + escapeHtml(PhoneNumber); }
    if (Price) { title += ', ' + Intl.NumberFormat(this.$i18n.locale, {
            maximumFractionDigits: DEFAULT_NUMBER_DECIMAL.minimumFractionDigits
        }).format(Price) + ' ' + escapeHtml(this.$td(props, 'PriceUnitDesc')); }
    if (!title) title = `${Id}`;

    // If no query, return title without highlighting
    if (!query) return title;

    // Escape the query to prevent regex injection, then highlight
    const escapedQuery = escapeHtml(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return title.replace(new RegExp(escapedQuery, 'gi'), '<mark>$&</mark>');
}

export function mark(value: string, q: string) {
    // Nothing to highlight while the query is empty (e.g. input just cleared) or the value is missing.
    if (!value || !q) return value;
    let query = q.replace(/[&\/\\#,+()$~%.'":*?<>{}]/g, `[$&]`);
    return value.replace(new RegExp(query, 'i'), `<mark>$&</mark>`);
}
export function* handleTitleCase(value: string, type: number) {
    if (!value) return;
    switch (type) {
        case 1:
            yield value.toUpperCase();
            break;
        case 2:
            yield value.toLowerCase().replace(/\b(\w)/g, s => s.toUpperCase());
            break;
        default:
            yield value;
            break;
    }
}

export function convertHREF(value: string) {
    if (!value) return;
    return value.replace(/(<href[^>]*="?([^"]*)"?[>])([a-zA-Z0-9\s\-!$%^&*()_+|~=`{}\[\]:\/;<>?,.@#]+)(<\/href>)/g, "<a target='_blank' href=\"$2\">$3</a>");
}

export function* getStyles(item: string) {
    if (!item) yield [];
    yield (item.match(/\.[^]\w+({[^}*]*})(\n|\r\n)/g) || [])
        .map((c) => ({ class: c.replace(/\.([^]\w+)({[^}*]*})(\n|\r\n)/g, 'class="$1"'), style: c.replace(/\.[^]\w+{([^}*]*)}(\n|\r\n)/g, 'style="$1"') }));
}

export function getCacheKeyByModule(key: string) {
    let cacheKey = key.replace(/\/$/g, "");
    return {
        cacheKey: cacheKey,
        cacheKeyOrigin: `${cacheKey}-origin`,
        cacheKeyCondition: `${cacheKey}-condition`,
        cacheKeyFilter: `${cacheKey}-filter`,
        cacheKeyFilterParameter: `${cacheKey}-filter-parameter`,
    };
}

export function cacheClear(key: string) {
    let { cacheKey, cacheKeyOrigin, cacheKeyCondition, cacheKeyFilter, cacheKeyFilterParameter } = getCacheKeyByModule(key);
    LocalforageWrapper.removeItem(cacheKey);
    LocalforageWrapper.removeItem(cacheKeyOrigin);
    LocalforageWrapper.removeItem(cacheKeyCondition);
    LocalforageWrapper.removeItem(cacheKeyFilter);
    LocalforageWrapper.removeItem(cacheKeyFilterParameter);
}

export function cacheUpdateOrCreate(key: string, object: Object, module?: number) {
    let keyGenerate: string = null;
    switch (module) {
        case moduleConstant.Task:
            let { cacheKeyCondition } = getCacheKeyByModule(key);
            keyGenerate = cacheKeyCondition;
            break;
        default:
            let { cacheKey } = getCacheKeyByModule(key);
            keyGenerate = cacheKey;
            break;
    }
    return LocalforageWrapper.setItem(keyGenerate, object).then((result) => {
        return Promise.resolve(result);
    });
}

export function isCache(module): boolean {
    switch (module) {
        case moduleConstant.Task:
            return !!CACHE_KEY.TASK.CACHE;
        case moduleConstant.Agenda:
            return !!CACHE_KEY.AGENDA.CACHE;
        case moduleConstant.Demand:
            return !!CACHE_KEY.DEMAND.CACHE;
        case moduleConstant.Person:
            return !!CACHE_KEY.CLIENT.CACHE;
        case moduleConstant.Object:
            return !!CACHE_KEY.PROPERTY.CACHE;
        case moduleConstant.Project:
            return !!CACHE_KEY.PROJECT.CACHE;
        default:
            return false;
    }
}

export function groupBy(items: Array<any>, name: string) {
    return items.reduce((groups, item) => {
        let value = item[name];
        groups[value] = groups[value] || [];
        groups[value].push(item);
        return groups;
    }, []);
}

export function calculatorDivisionTotal(items: Array<any>, key: string, keyValue: number, value: string) {
    let result: Array<number> = items.filter(item => item[key] === keyValue).map(item => item[value] || 0);
    return result.reduce((total, item) => total + item, 0);
}

export function addPrefixForEvent(value, route: Route) {
    return value.replace(":prefix", route.path.replace(/\/$/, "").replace(/\//g, "-"));
}

/**
 * Description: Get param from url query
 * @param url 
 */
export function getParams(url) {
    let params = {};
    let parser = document.createElement('a');
    parser.href = url;
    let query = parser.search.substring(1);
    let vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
        let pair = vars[i].split('=');
        params[pair[0]] = decodeURIComponent(pair[1]);
    }
    return params;
}

export function convertObjectKeyToArrayString(obj) {
    if (typeof obj != 'object') return [];
    let str = [];
    for (const key in obj) {
        str.push(obj[key]);
    }
    return str;
}

export function getMainTabFromPath(path) {
    if (typeof path != 'string' || path == '') return '';
    return path.split('/').filter(item => item != null && item != "")[0];
}

export const getPropertyIcon = (constructionType) => {
    switch (constructionType) {
        case 0:
            return 'ico_home'
        case 1:
            return 'ico_project'
        case 2:
            return 'o_icon_unit_np'
        case 3:
            return 'o_ico_garage_np'
        default:
            return 'ico_home'
    }
}

export const getBaseIconResourceUrl = () => {
    const storageLink = localStorage.getItem(LOCALSTORAGE_BASE_ICON_RESOURCE_URL)
    return storageLink ? storageLink : RESOURCE_LINKS.OBJECTSTORAGE_WEBAPP_ICON_URL
}

export const copyToClipBoard = (value) => {
    const input = document.createElement("input");
    input.setAttribute('aria-hidden', 'true');
    input.className = 'offscreen';
    input.value = value;
    document.body.appendChild(input)
    input.select()
    document.execCommand("copy")
    document.body.removeChild(input)
}

export const getFieldNameByLocale = (fiellName) => {
    return fiellName + i18n.locale.substring(0, 2).toLocaleUpperCase();
}
export function isURL(str) {
    const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
    return !!pattern.test(str);
}

export const buildOmnicasaMapUrl = (
    houseNumber,
    cityPostcode,
    address,
    cityName,
    googleX,
    googleY,
    language,
    isGetHeritage
) => {
    let url = OMNICASA_MAP_BASE_URL;
    if(localStorage.getItem(LOCALSTORAGE_ENV) === "dev")
        url = url.replace('Page','test/Page');

    return `${url}?` + 
    `HOUSENUMBER=${houseNumber || ''}&` +
    `POSTCODE=${cityPostcode || ''}&` +
    `STREET=${address || ''}&` +
    `CITY=${cityName || ''}&` + 
    `GOOGLEX=${googleX || 0}&` + 
    `GOOGLEY=${googleY || 0}&` +
    `LANGUAGE=${language}&` +
    `GETHERITAGES=${isGetHeritage}`
}

export const getLangId = () => {
    let lang = localStorage.getItem('lang');
    if (lang === 'nl-BE')
        return 1;
    if (lang === 'fr-BE')
        return 2;
    return 3;
}

export const findTabViewHandler = (tab, compareTab) => {
    return tab.identifyData.name === compareTab.identifyData.name 
    && tab.identifyData.id == compareTab.identifyData.id; // use double equal on purpose
}

export const getPropertyViewRouteByConstructionType = ({ConstructionType, Id, ProjectId}) => {
    switch (ConstructionType) {
        case CONSTRUCTION_TYPE.Project:
            return {
                name: ROUTER.PROPERTY.PROPERTY_PROJECT_VIEW,
                params: {
                    id: ProjectId
                }
            } 
        default:
            return {
                name: ROUTER.PROPERTY.PROPERTY_VIEW,
                params: {
                    id: Id
                }
            } 
    }
}
export const getPropertyEditRouteByConstructionType = ({ConstructionType, Id, ProjectId}) => {
    switch (ConstructionType) {
        case CONSTRUCTION_TYPE.Project:
            return {
                name: ROUTER.PROJECT.PROJECT_EDIT,
                params: {
                    id: ProjectId
                }
            } 
        default:
            return {
                name: ROUTER.PROPERTY.PROPERTY_EDIT,
                params: {
                    id: Id
                }
            } 
    }
}

export const buildQueryForQuickView = (type: string, id: any) => {
    return {
        qv_type: type,
        qv_id: id
    }
}

export const buildAddressString = (address, houseNumber, boxNumber, postCode, cityName, countryName) => {
	let arr1 = compact([address, houseNumber, boxNumber]) as Array<string>
	let arr2 = compact([postCode, cityName]) as Array<string>
	let resultArray = compact([arr1.join(" "), arr2.join(" "), countryName]) as Array<string>
	let result = resultArray.join(", ")
	if (result == countryName) return null;
	return result;
}

// A ready (token'd) publish link from the WebApi â€” must be used as-is; the
// publish-document round trip only resolves relative paths (FRO-11327)
export const isAbsoluteUrl = (url: any): boolean =>
    typeof url === "string" && /^https?:\/\//i.test(url);

const getFileType = (url: string) => {
    if (url == null || url == "") return "";
    // Strip the query/fragment first â€” publish links carry a token query whose
    // base64 contains dots, which made the "extension" the token signature (FRO-11327)
    let cleanUrl = url.split(/[?#]/)[0];
    let splitIndex1 = cleanUrl.lastIndexOf(".");
    if (splitIndex1 < 0) return "";
    return cleanUrl.slice(splitIndex1);
}

export const detectFileType = {
    isPictureLink: (url: string) => {
        let type = getFileType(url);
        let result = /\.(gif|jpg|jpeg|tiff|png)$/i.test(type);
        return result;
    },
    isDocumentLink: (url: string) => {
        let type = getFileType(url);
        let rg = new RegExp("(doc|docx|xls|xlsx|pdf|txt|ppt|pptx|ht|html)",'i');
        let result = rg.test(type);
        return result;
    },
    isWordFile: (url: string) => {
        let type = getFileType(url);
        let rg = new RegExp("(doc|docx)", 'i');
        let result = rg.test(type);
        return result;
    },
    isExcelFile: (url: string) => {
        let type = getFileType(url);
        let rg = new RegExp("(xls|xlsx)", 'i');
        let result = rg.test(type);
        return result;
    },
    isPowerPointFile: (url: string) => {
        let type = getFileType(url);
        let rg = new RegExp("(ppt|pptx)", 'i');
        let result = rg.test(type);
        return result;
    },
    isPdfFile: (url: string) => {
        let type = getFileType(url);
        let rg = new RegExp("(pdf)", 'i');
        let result = rg.test(type);
        return result;
    },
    isHTMLFile: (url: string) => {
        let type = getFileType(url);
        let rg = new RegExp("(htm|html)", 'i');
        let result = rg.test(type);
        return result;
    },
    isTxtFile: (url: string) => { 
        let type = getFileType(url);
        let rg = new RegExp("(txt)", 'i');
        let result = rg.test(type);
        return result;
    },
    canOpenDirectInFrame(url) {
        let type = getFileType(url);
        let rg = new RegExp("(txt|pdf|htm|html)", 'i');
        let result = rg.test(type);
        return result;
    },
    isHtmlFile: (url: string) => { 
        let type = getFileType(url);
        let rg = new RegExp("(html)", 'i');
        let result = rg.test(type);
        return result;
    },
    isZipFile: (url: string) => { 
        let type = getFileType(url);
        let rg = new RegExp("(zip)", 'i');
        let result = rg.test(type);
        return result;
    }
}


export const viewErrorInTheOtherPlaces = (scope: string, fieldName: string, activeTabName: string = null) => {
    try {
        
        let field = TAB_TREE.find(tab => {
            return tab.SCOPE === scope && tab.FIELDS.indexOf(fieldName) > -1;
        });

        if (field && (!activeTabName || field.MAIN_TAB_NAME !== activeTabName) && document.getElementById(field.MAIN_TAB_ID)) {
            document.getElementById(field.MAIN_TAB_ID).click();
            if ( field.SUB_TAB_ID) {
                setTimeout(() => {

                    if (document.getElementById(field.SUB_TAB_ID)) {
                        document.getElementById(field.SUB_TAB_ID).click();
                    }
                }, 1000);
            }
        }
    } catch (error) {
        console.log(error);
    }
}

export const calcTableHeight = (data: any[], maxHeight: number, rowHeight: number = 30, minHeight: number = 130, ) => {

    let tableHeight: number = minHeight;
    if (data && data.length > 0) {
        tableHeight = data.length * rowHeight + 100; // 100 for pagination
    }
    if (maxHeight && tableHeight > maxHeight) {
        return maxHeight
    }
    return tableHeight;
}

export const isValidDate = (val: string) => {
    if (!val) return false;
    return moment(val).isValid();
}

export const isValidEmail = (val: string) => {
    if (!val) return false;
    return /^[a-zA-Z0-9.!#$%&â€™*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(val.toLocaleLowerCase());
}
export const isPhoneNumber = (val) => {
    if (!val) return false;
    return /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/.test(val);
}
export const formatPrice = (value) => {
    const decimalConfig = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
    const langCode = localStorage.getItem("lang");
    return `${value.toLocaleString(langCode, decimalConfig)}`
}

export const findHighestZIndex = (elem) => {
    let elems = document.getElementsByClassName(elem);
    let highest = Number.MIN_SAFE_INTEGER || -(Math.pow(2, 53) - 1);
    for (let i = 0; i < elems.length; i++) {
        let zindex = Number.parseInt(document.defaultView.getComputedStyle(elems[i], null).getPropertyValue("z-index"), 10);
        if (zindex > highest) {
            highest = zindex;
        }
    }
    return highest < 0 ? 102 : highest; // z-index: 102 is the highgest without dialog, pppover, tooltip...
}

export const dynamicWidthForPopover = (data, fieldName: string, plusText?: string) => {
    const maxLengthText = (data, fieldName) => {
        if (!data || data.length === 0) return 0;
        return Math.max.apply(Math, data.map(item => {
            let txt = item[getFieldNameByLocale(fieldName)] || item[fieldName];
            return txt ? txt.length : 0;
        }));
    }

    let btnText = plusText ? plusText.length : 0;
    const padding = 130;
    const chracterWidth = 7;
    const min = 350;
    const width = ((maxLengthText(data, fieldName) + btnText) * chracterWidth) + padding;
    
    return width < min ? min : width;
}

export const insertCharacterAtCursorPositionOfTextArea = (refTextarea, value) => {
    let tArea = refTextarea.$refs ? refTextarea.$refs.textarea : refTextarea;
    let startPos = tArea.selectionStart;
    let endPos = tArea.selectionEnd;
    let cursorPos = startPos;
    let tmpStr = tArea.value;

    // insert new character:
    const result = tmpStr.substring(0, startPos) + value + tmpStr.substring(endPos, tmpStr.length);
    
    // set cursor after new character:
    setTimeout(() => {
        cursorPos += value.length;
        tArea.selectionStart = tArea.selectionEnd = cursorPos;
    }, 10);
    return result;
}

export const convertDecimalCodeToEmoji = (text) => {
    let result = text;
    let matchList = text.match(/(\&\#([0-9]*)\;)/gm);
    if (matchList && matchList.length > 0) {
        matchList = matchList.filter((v,i, self) => {
            return self.indexOf(v) === i;
        });
        
        matchList.forEach(el => {
            result = result.replaceAll(el, String.fromCodePoint(el.slice(2,-1)))
        });
    }
    return result;
}

export const convertEmojiToDecimal = (text) => {
    let result = text;
    let reg = new RegExp(/(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g);
    let emojiList = text.match(reg);
    if (emojiList && emojiList.length > 0) {
        emojiList = emojiList.forEach(e => {
            result = result.replaceAll(e, `&#${e.codePointAt(0).toString()};`)
        });
    }        
    return result;
}

export const formatPhoneNumberForKorfine1 = (phoneNumber) => {
    let startWithPlusSign = false;
    if (phoneNumber[0] == '+') {
        startWithPlusSign = true;
    }
    phoneNumber = phoneNumber.replaceAll(/\D/g, "");

    if (startWithPlusSign) {
        return '+' + phoneNumber;
    }
    return phoneNumber;
}

export const formatPhoneNumberForKorfine = (phoneNumber) => {
    phoneNumber = formatPhoneNumberForKorfine1(phoneNumber)
    let belgiumPhoneNumber = formatForeignBelgiumPhoneNumber(phoneNumber,'');
    return formatPhone(belgiumPhoneNumber);
}

export const formatForeignBelgiumPhoneNumber = (strPhoneNumber, phoneSeparator = '') => {
	if (!strPhoneNumber || !isPhoneNumber(strPhoneNumber))
		return strPhoneNumber;
	if (strPhoneNumber.startsWith("00") || strPhoneNumber.startsWith("+"))
		return strPhoneNumber;
	if (strPhoneNumber.startsWith("0"))
		strPhoneNumber = strPhoneNumber.substring(1);
    let phoneTemplate = '+32{0}{1}';
    let phoneNumber = phoneTemplate.replace('{0}', phoneSeparator);
    phoneNumber = phoneNumber.replace('{1}', strPhoneNumber);
	return phoneNumber;
}

export const formatPhone = (phone) => {
	if (!phone) return "";
	// let textFirst = (phone.Length > 3) ? phone.substring(0, 2) : phone;
	// if (phone && textFirst.includes("00"))
	// 	return phone.substring(2);
        return phone;
}

/**
 * Format Belgian phone numbers for display with dot separators
 * Only formats Belgian numbers (+32, 0032, or local 02/03/04/09)
 * Other numbers are returned as-is
 * Examples:
 *   +32475123456 -> +32.475.12.34.56
 *   0032475123456 -> +32.475.12.34.56
 *   0475123456 -> 0475.12.34.56
 *   0988708663 -> 0988708663 (non-Belgian, kept as-is)
 */
export const formatPhoneNumberForDisplay = (phone: string): string => {
    if (!phone) return "";

    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');

    if (!cleaned) return phone;

    // Convert 0032 prefix to +32 (Belgian international format)
    if (cleaned.startsWith('0032')) {
        cleaned = '+32' + cleaned.substring(4);
    }

    // Handle Belgian phone numbers (+32)
    if (cleaned.startsWith('+32')) {
        const rest = cleaned.substring(3);
        if (rest.length >= 9) {
            // Mobile: +32.4XX.XX.XX.XX
            if (rest.startsWith('4')) {
                return `+32.${rest.substring(0, 3)}.${rest.substring(3, 5)}.${rest.substring(5, 7)}.${rest.substring(7, 9)}${rest.length > 9 ? '.' + rest.substring(9) : ''}`;
            }
            // Landline: +32.X.XXX.XX.XX or +32.XX.XX.XX.XX
            const areaCode = rest.substring(0, 1);
            if (['2', '3', '9'].includes(areaCode)) {
                // Single digit area code (Brussels, Antwerp, Ghent)
                return `+32.${rest.substring(0, 1)}.${rest.substring(1, 4)}.${rest.substring(4, 6)}.${rest.substring(6, 8)}${rest.length > 8 ? '.' + rest.substring(8) : ''}`;
            }
            // Two digit area code
            return `+32.${rest.substring(0, 2)}.${rest.substring(2, 4)}.${rest.substring(4, 6)}.${rest.substring(6, 8)}${rest.length > 8 ? '.' + rest.substring(8) : ''}`;
        }
        // Short Belgian number - just add dot after country code
        return `+32.${rest}`;
    }

    // Handle local Belgian format (only 02, 03, 04, 09 prefixes)
    if (cleaned.length >= 9 && cleaned.length <= 10) {
        // Belgian mobile: 04XX.XX.XX.XX
        if (cleaned.startsWith('04')) {
            return `${cleaned.substring(0, 4)}.${cleaned.substring(4, 6)}.${cleaned.substring(6, 8)}.${cleaned.substring(8, 10)}${cleaned.length > 10 ? '.' + cleaned.substring(10) : ''}`;
        }
        // Belgian landline with single digit area code (02, 03, 09)
        if (['02', '03', '09'].includes(cleaned.substring(0, 2))) {
            return `${cleaned.substring(0, 2)}.${cleaned.substring(2, 5)}.${cleaned.substring(5, 7)}.${cleaned.substring(7, 9)}${cleaned.length > 9 ? '.' + cleaned.substring(9) : ''}`;
        }
    }

    // All other numbers: return as-is
    return phone;
}

// A cleared date picker gives null; moment would turn that into the string "Invalid date".
export const formatDateKorfine = (date) => {
    return date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : null;
}

// A Korfine partner is considered absent when none of its fields hold a value.
export const isEmptyKorfinePerson = (person?: object | null): boolean => {
    return !person || !Object.values(person).some(value => value !== null && value !== undefined && value !== '');
}

export const isBelgiumNationality = (locale: string, nationality: string) => {
    return ((locale === 'de-BE' && nationality === 'Belgier') ||
        (locale === 'fr-BE' && nationality === 'Belge') ||
        (locale === 'nl-BE' && nationality === 'Belg'))
}

export const getBelgiumNameByLocale = (locale: string) => {
    return {
        'en-US': 'Belgium',
        'fr-BE': 'Belgique',
        'de-BE': 'Belgien',
        'nl-BE': 'BelgiÃ«'
    }[locale];
}

export const PromiseHelper = {
    allSettled(promises: Promise<any>[]) {
        const mapPromises = promises.map(promise => Promise.resolve(promise).then(
            value => ({status: 'fulfilled', value}),
            err => ({status: 'rejected', value: err})
        ));
        return Promise.all(mapPromises);
    }
}